import { z } from 'zod';

/**
 * Recursively removes default values from a Zod schema.
 *
 * This is useful when introspecting schema shapes without default wrappers,
 * such as when building dynamic property lists from view schemas where
 * default values would obscure the underlying structure.
 *
 * @example
 * ```ts
 * const schema = z.object({
 *   name: z.string().default('unnamed'),
 *   count: z.number().optional(),
 * });
 *
 * const unwrapped = deepRemoveDefaults(schema);
 * // Now `unwrapped.shape` contains the base types without default wrappers
 * ```
 */
export function deepRemoveDefaults(schema: z.ZodTypeAny): z.ZodTypeAny {
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal properties (.shape, ._def, .removeDefault) not in public types
  const schemaAny = schema as any as Record<string, unknown>;

  // ZodDefault wrapper - unwrap to get underlying schema
  if ('removeDefault' in schemaAny && typeof schemaAny.removeDefault === 'function') {
    return deepRemoveDefaults((schemaAny.removeDefault as () => z.ZodTypeAny)());
  }

  // ZodOptional/ZodNullable wrappers - recursively unwrap
  if ('unwrap' in schemaAny && typeof schemaAny.unwrap === 'function') {
    if (schemaAny.type === 'lazy') {
      // For lazy schemas, we cannot safely unwrap without risking recursion errors, so we return the unwrapped schema directly without further processing.
      return deepRemoveDefaults(schemaAny.unwrap());
    }

    return deepRemoveDefaults((schemaAny.unwrap as () => z.ZodTypeAny)());
  }

  // ZodObject - recursively process each field in the shape
  if ('shape' in schemaAny && typeof schemaAny.shape === 'object' && schemaAny.shape !== null) {
    const shape = schemaAny.shape as Record<string, z.ZodTypeAny>;
    const newShape: Record<string, z.ZodTypeAny> = {};
    for (const key in shape) {
      if (!shape[key]) {
        continue;
      }
      newShape[key] = deepRemoveDefaults(shape[key]);
    }
    return z.object(newShape);
  }

  // ZodArray - recursively process the element type
  if ('element' in schemaAny && schemaAny.element) {
    return z.array(deepRemoveDefaults(schemaAny.element as z.ZodTypeAny));
  }

  // Base case: primitive types or unsupported wrappers remain unchanged
  return schema;
}
